import { Dispatch } from 'redux';
import { PlantDto, ProductLine, TicketDto } from '@trucktrax/trucktrax-ts-common';
import { getPlant } from './plantsService';
import { getIdFromUrl } from '../util/appUtil';
import { devErrorAndLog } from '../util/errorUtil';
import { ERROR_TEXT_RESOLVE_TICKET_PRODUCT_LINE } from '../constants/errorConstants';

// DEV-3924: the Route Info status tree must key off the ORIGINATION plant's product line
// (ticket.plant), never the globally selected product line. `externalInfo.plantProductLine`
// is recorded server-side at ticketing time from the origination plant, so it is the primary
// source; `ProductLine.None` means "unknown" and is treated as unresolved.

// Session-lifetime cache (no eviction): a plant's product line effectively never changes
// mid-session, and a stale entry self-heals on page reload. Only successful resolutions
// are cached, so transient fetch failures retry on the next ticket open.
const plantProductLineCache = new Map<string, ProductLine>();

export const clearTicketProductLineCache = (): void => {
  plantProductLineCache.clear();
};

/**
 * Resolves the product line of the ticket's origination plant.
 * Resolution chain: externalInfo.plantProductLine (≠ None) → fetch origination plant → undefined.
 * `undefined` means unresolved — callers must hide the status tree rather than fall back to
 * the selected product line.
 */
export function resolveTicketProductLine(ticket: TicketDto) {
  return async (dispatch: Dispatch): Promise<ProductLine | undefined> => {
    const fromTicket = ticket?.externalInfo?.plantProductLine;
    if (fromTicket && fromTicket !== ProductLine.None) {
      return fromTicket;
    }

    // consoleOnly — getPlant already surfaces fetch failures in a dev modal; this entry
    // records the unresolved outcome (and its cause) to telemetry without doubling up.
    const logUnresolved = (reason: string): undefined => {
      dispatch(
        devErrorAndLog(
          ERROR_TEXT_RESOLVE_TICKET_PRODUCT_LINE,
          `resolveTicketProductLine: ${reason}`,
          undefined,
          `ticket: ${ticket?.url} plant: ${ticket?.plant?.url}`,
          true,
        ) as any,
      );
      return undefined;
    };

    const plantUrl = ticket?.plant?.url;
    if (!plantUrl) {
      return logUnresolved('ticket has no origination plant url');
    }

    const cached = plantProductLineCache.get(plantUrl);
    if (cached) {
      return cached;
    }

    const plant: PlantDto | undefined = await dispatch(
      getPlant(Number(getIdFromUrl(plantUrl)), ticket?.region?.url) as any,
    );
    const plantProductLine = plant?.productLine;
    if (plantProductLine && plantProductLine !== ProductLine.None) {
      // Only successes are cached, so a transient fetch failure can recover next time.
      plantProductLineCache.set(plantUrl, plantProductLine);
      return plantProductLine;
    }

    return logUnresolved(
      plant ? 'origination plant has no usable product line' : 'origination plant fetch failed',
    );
  };
}
